import React from 'react';
import { BrandingProvider } from './hooks/useBranding';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './hooks/useAuth';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Customers from './pages/Customers';
import Routers from './pages/Routers';
import Packages from './pages/Packages';
import Transactions from './pages/Transactions';
import Bills from './pages/Bills';
import Users from './pages/Users';
import SPOPs from './pages/SPOPs';
import SPOPForm from './pages/SPOPForm';
import ODCs from './pages/ODCs';
import ODCForm from './pages/ODCForm';
import ODPs from './pages/ODPs';
import OLTs from './pages/OLTs';
import OLTDashboard from './components/OLTDashboard';
import WhatsappSettings from './pages/WhatsappSettings';
import WhatsappBroadcast from './pages/WhatsappBroadcast';
import WhatsappBroadcastHistory from './pages/WhatsappBroadcastHistory';
import WhatsappQueueLog from './pages/WhatsappQueueLog';
import SettingsPage from './pages/SettingsPage';
import PublicPaymentPage from './pages/PublicPaymentPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import PaymentStatusPage from './pages/PaymentStatusPage';
import PaybosPublicPaymentPage from './pages/PaybosPublicPaymentPage';
import PaybosPaymentSuccessPage from './pages/PaybosPaymentSuccessPage';
import SPOPRevenue from './pages/SPOPRevenue';
import SchedulerManagement from './pages/SchedulerManagement';
import BrandingSettings from './components/BrandingSettings';
import CsApiSettingsPage from './pages/CsApiSettingsPage';

// Create theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
});

// Create QueryClient
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// App Routes Component
const AppRoutes: React.FC = () => {
  const { isAuthenticated } = useAuth();

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/payment/bill/:billId" element={<PublicPaymentPage />} />
      <Route path="/payment/paybos/bill/:billId" element={<PaybosPublicPaymentPage />} />
      <Route path="/payment/paybos/success" element={<PaybosPaymentSuccessPage />} />
      <Route path="/payment/:reference" element={<PaymentStatusPage />} />
      <Route path="/payment/success/:reference" element={<PaymentSuccessPage />} />
      
      <Route 
        path="/login" 
        element={
          isAuthenticated ? <Navigate to="/" replace /> : <Login />
        } 
      />
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <Layout>
              <Routes>
                <Route path="/" element={<Dashboard />} />
                <Route path="/dashboard" element={<Navigate to="/" replace />} />
                <Route path="/customers" element={<Customers />} />
                <Route path="/routers" element={<Routers />} />
                <Route path="/packages" element={<Packages />} />
                <Route path="/transactions" element={<Transactions />} />
                <Route path="/bills" element={<Bills />} />
                
                {/* Reports Routes */}
                <Route path="/reports/spop-revenue" element={<SPOPRevenue />} />
                
                {/* User Management Routes - Admin Only */}
                <Route
                  path="/users"
                  element={
                    <ProtectedRoute requiredRole="admin">
                      <Users />
                    </ProtectedRoute>
                  }
                />
                
                {/* Master Data Routes */}
                <Route path="/master-data/spop" element={<SPOPs />} />
                <Route path="/master-data/spop/create" element={<SPOPForm />} />
                <Route path="/master-data/spop/edit/:id" element={<SPOPForm />} />
                
                <Route path="/master-data/odc" element={<ODCs />} />
                <Route path="/master-data/odc/create" element={<ODCForm />} />
                <Route path="/master-data/odc/edit/:id" element={<ODCForm />} />
                
                <Route path="/master-data/odp" element={<ODPs />} />
                
                {/* OLT Routes */}
                <Route path="/olts" element={<OLTs />} />
                <Route path="/olts/dashboard" element={<OLTDashboard />} />
                
                {/* Settings Routes */}
                <Route path="/settings" element={<Navigate to="/settings/tripay" replace />} />
                <Route path="/settings/tripay" element={<SettingsPage />} />
                <Route path="/settings/paybos" element={<SettingsPage />} />
                <Route path="/settings/whatsapp" element={<WhatsappSettings />} />
                <Route
                  path="/settings/scheduler"
                  element={
                    <ProtectedRoute requiredRole="admin">
                      <SchedulerManagement />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings/branding"
                  element={
                    <ProtectedRoute requiredRole="admin">
                      <BrandingSettings />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings/cs-api"
                  element={
                    <ProtectedRoute requiredRole="admin">
                      <CsApiSettingsPage />
                    </ProtectedRoute>
                  }
                />
                
                {/* WhatsApp Routes */}
                <Route path="/whatsapp/broadcast" element={<WhatsappBroadcast />} />
                <Route path="/whatsapp/broadcast/history" element={<WhatsappBroadcastHistory />} />
                <Route path="/whatsapp/queue/log" element={<WhatsappQueueLog />} />
                
                {/* Add more protected routes here */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Layout>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <BrandingProvider>
          <AuthProvider>
            <Router>
              <AppRoutes />
            </Router>
          </AuthProvider>
        </BrandingProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
